import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, X, FileText, Image, Film, File as FileIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const MAX_SIZE = 45 * 1024 * 1024; // 45MB

function formatSize(bytes) {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(type) {
  if (!type) return FileIcon;
  if (type.startsWith('image/')) return Image;
  if (type.startsWith('video/')) return Film;
  if (type.includes('pdf') || type.includes('document') || type.includes('text')) return FileText;
  return FileIcon;
}

export function FileUpload({ onUploadComplete, className = '' }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  const handleSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_SIZE) {
      toast.error(`File too large (${formatSize(file.size)}). Maximum size is 45MB.`);
      e.target.value = '';
      return;
    }

    setSelectedFile(file);

    // Generate preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const r = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploadComplete(r.data);
      clearFile();
      toast.success('File uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const Icon = selectedFile ? getFileIcon(selectedFile.type) : Upload;

  
  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        onChange={handleSelect}
        className="hidden"
        data-testid="file-upload-input"
      />

      {!selectedFile ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full border-2 border-dashed border-border rounded-lg p-6 flex flex-col items-center gap-2 hover:border-primary/50 hover:bg-primary/5 transition-colors cursor-pointer"
          data-testid="file-upload-dropzone"
        >
          <Upload className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Click to select a file</p>
          <p className="text-xs text-muted-foreground/60">Maximum file size: 45MB</p>
        </button>
      ) : (
        <div className="border border-border rounded-lg p-3 space-y-3" data-testid="file-upload-preview">
          <div className="flex items-start gap-3">
            {preview ? (
              <img src={preview} alt="Preview" className="h-16 w-16 rounded-md object-cover border border-border shrink-0" />
            ) : (
              <div className="h-16 w-16 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                <Icon className="h-7 w-7 text-primary" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{selectedFile.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {formatSize(selectedFile.size)} &middot; {selectedFile.type || 'Unknown type'}
              </p>
              {selectedFile.size > MAX_SIZE && (
                <p className="text-xs text-destructive mt-1">File exceeds 45MB limit</p>
              )}
            </div>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground shrink-0" onClick={clearFile} data-testid="file-upload-clear">
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleUpload}
              disabled={uploading || selectedFile.size > MAX_SIZE}
              className="bg-primary text-primary-foreground flex-1 gap-1.5"
              data-testid="file-upload-submit"
            >
              {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} className="text-xs">
              Change
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
